import { fetchUser } from '@/api/user';
import { callPostApi } from '@/components/api-common';
import {
  Product,
  PaymentSource,
  Invoice,
  EstimateResult,
  Subscription,
  Interval,
  Coupon,
  Phase,
} from './models';
import { MOCK_PRODUCTS } from './data/mock-data';

/**
 * set window.__MOCK__ before loading the page,
 * otherwise will get mismatched current subscription and products -> error
 * can be fixed though
 */
async function getAllProducts() {
  let res: Product[];
  if (!window.__MOCK__) {
    res = (await callPostApi('/user/service/subs/subs_products/')) as Product[];
  } else {
    res = MOCK_PRODUCTS;
  }
  const FREE_PRODUCT: Product = {
    id: 'Free',
    level_name: 'FREE',
    name: 'Free',
    stripe_id: '',
    plans: [],
  };
  return [FREE_PRODUCT, ...res];
}

export interface CouponInfo {
  coupon?: {
    id: string;
  };
}
async function getCoupons() {
  return callPostApi(
    '/user/service/subs/prefill_coupon/'
  ) as Promise<CouponInfo>;
}
async function getPaymentResources() {
  let res: PaymentSource[];
  if (!window.__MOCK__) {
    res = (await callPostApi(
      '/user/service/subs/payment/payment_methods/'
    )) as PaymentSource[];
  } else {
    res = [
      {
        type: 'card',
        brand: 'MasterCard',
        is_default: true,
        last4: '2341',
        exp_month: 10,
        exp_year: 2012,
        address_zip: '222011',
      },
    ];
  }
  return res;
}

async function getUpcomingInvoice() {
  let res: Invoice;
  if (!window.__MOCK__) {
    res = (await callPostApi('user/service/subs/invoice/upcoming/')) as Invoice;
  } else {
    res = {
      created: 13212321321,
      subscription_proration_date: null,
      id: '1',
      stripe_id: '1',
      subtotal: 123,
      total: 456000,
      status: 'paid',
      subscription_id: '1',
      discount: null,
      period: { start: 123213123213, end: 123123123123123 },
      next_payment_attempt: 123,
      line_items: [
        {
          description: 'Education discount (50% off)',
          amount: -180,
          period: { start: 'def', end: 'abc' },
          quantity: 2,
          plan: {
            id: 'sdf',
            active: true,
            amount: 123,
            stripe_id: 'sdf',
            interval: 'month',
            interval_count: 123,
            product_id: '1',
          },
        },
      ],
    };
  }
  return res;
}
async function estimateInvoice(payload: {
  level_name: string;
  interval: Interval;
  quantity: number;
  coupon?: string;
  company?: string;
}) {
  let res: EstimateResult;
  if (!window.__MOCK__) {
    res = (await callPostApi(
      'user/service/subs/invoice/estimate/',
      payload
    )) as EstimateResult;
  } else {
    res = {
      now_invoice: {
        created: 13212321321,
        period: { start: 123213123213, end: 123123123123123 },
        discount: null,
        id: '1',
        stripe_id: '1',
        subscription_proration_date: null,
        subtotal: 100,
        total: 200,
        status: 'paid',
        subscription_id: '1',
        next_payment_attempt: 123,
        line_items: [],
      },
      next_invoice: {
        created: 13212321321,
        period: { start: 123213123213, end: 123123123123123 },
        subscription_proration_date: null,
        discount: null,
        id: '2',
        stripe_id: '1',
        subtotal: 100,
        total: 200,
        status: 'paid',
        subscription_id: '1',
        next_payment_attempt: 123,
        line_items: [
          {
            description: 'Education discount (50% off)',
            amount: -180,
            period: { start: 'def', end: 'abc' },
            quantity: 2,
            plan: {
              id: 'sdf',
              active: true,
              amount: 123,
              stripe_id: 'sdf',
              interval: 'month',
              interval_count: 123,
              product_id: '1',
            },
          },
        ],
      },
    };
  }
  return res;
}

async function getSubscriptions() {
  let res: Subscription[];
  if (!window.__MOCK__) {
    res = (await callPostApi(
      'user/service/subs/subscriptions/'
    )) as Subscription[];
  } else {
    res = [
      {
        id: '1',
        stripe_id: '2',
        items: [
          {
            quantity: 5,

            id: 'str',
            stripe_id: 'str',
            plan: {
              id: 'a',
              active: true,
              amount: 123,
              stripe_id: 'sdf',
              interval: 'month',
              interval_count: 123,
              product_id: '1',
            },
          },
        ],
        // cancel_at: 123,
        cancel_at: null,
        canceled_at: 123,
        current_period: { start: 1, end: 2 },
        schedule: {
          id: 'test',
          stripe_id: 'test',
          subscription_id: '',
          current_phase_period: { start: 1, end: 2 },
          phases: [
            {
              period: { start: 222222222222, end: 3333333333333333 },
              plans: [
                {
                  quantity: 2,
                  plan: {
                    id: 'a',
                    active: true,
                    amount: 123,
                    stripe_id: 'sdf',
                    interval: 'month',
                    interval_count: 123,
                    product_id: '2',
                  },
                },
              ],
              coupon_id: '',
            },
            {
              period: { start: 222222222222, end: 3333333333333333 },
              plans: [
                {
                  quantity: 2,
                  plan: {
                    id: 'a',
                    active: true,
                    amount: 123,
                    stripe_id: 'sdf',
                    interval: 'month',
                    interval_count: 123,
                    product_id: '2',
                  },
                },
              ],
              coupon_id: '',
            },
          ],
        },
        discount: null,
        created: 123,
        status: 'active',
        metadata: {},
      },
    ];
  }

  return res;
}

export type PreviewSchedue = {
  current: Phase;
  now: Phase | null; // 立即生效的subscription （upgrade部分）
  next: Phase | null; // 下个周期会生效的subscription，可能为null（只有upgrade时），为空则表示和now相同
};

async function previewSchedule(payload: {
  level_name: string;
  quantity: number;
  interval: Interval;
  coupon: string;
  company_name?: string;
}) {
  let res;
  if (!window.__MOCK__) {
    res = await callPostApi('user/service/subs/schedule/preview/', payload);
  } else {
    const phase = {
      period: { start: 222222222222, end: 3333333333333333 },
      plans: [
        {
          quantity: 2,
          plan: {
            id: 'a',
            active: true,
            amount: 123,
            stripe_id: 'sdf',
            interval: 'month',
            interval_count: 123,
            product_id: '2',
          },
        },
      ],
      coupon_id: '',
    };
    res = {
      current: phase,
      next: phase,
      now: phase,
    };
  }
  return res as PreviewSchedue;
}

async function deleteSchedule() {
  let res;
  if (!window.__MOCK__) {
    res = await callPostApi('user/service/subs/schedule/delete/');
  }
  return res;
}

async function createOrUpdateSubscription(payload: {
  level_name?: string;
  quantity?: number;
  interval?: Interval;
  coupon?: string;
  company_name?: string;
  subscription_proration_date?: string | null;
  from_samsung_store?: boolean;
}) {
  let res;
  if (!window.__MOCK__) {
    res = await callPostApi(
      'user/service/subs/subscription/create_or_update/',
      payload
    );
  }
  return res as Subscription;
}
async function cancelSubscription() {
  let res;
  if (!window.__MOCK__) {
    res = await callPostApi('user/service/subs/subscription/cancel/');
  }
  return res;
}

async function renew() {
  let res;
  if (!window.__MOCK__) {
    res = await callPostApi('user/service/subs/subscription/dont_cancel/');
  }
  return res;
}
async function retryPay(invoiceId: string) {
  let res;
  if (!window.__MOCK__) {
    res = await callPostApi('user/service/subs/invoice/retry_for_failing/', {
      invoice_id: invoiceId,
    });
  }
  return res;
}

async function getLatestFailedInvoice(): Promise<Invoice> {
  let res;
  if (!window.__MOCK__) {
    res = (await callPostApi(
      'user/service/subs/invoice/latest_failed/'
    )) as Invoice;
  } else {
    res = {
      subscription_proration_date: null,
      id: '1',
      stripe_id: '1',
      subtotal: 123,
      total: 456000,
      status: 'paid',
      subscription_id: '1',
      discount: null,
      next_payment_attempt: 123,
      line_items: [
        {
          description: 'Education discount (50% off)',
          amount: -180,
          period: { start: 'def', end: 'abc' },
          quantity: 2,
          plan: {
            id: 'sdf',
            active: true,
            amount: 123,
            stripe_id: 'sdf',
            interval: 'month',
            interval_count: 123,
            product_id: '1',
          },
        },
        {
          description: 'Education discount (50% off)',
          amount: -180,
          period: { start: 'def', end: 'abc' },
          quantity: 2,
          plan: {
            id: 'dk',
            active: true,
            amount: 123,
            stripe_id: 'sdf',
            interval: 'month',
            interval_count: 123,
            product_id: '1',
          },
        },
      ],
    } as Invoice;
  }
  return res;
}
async function getUser() {
  return await fetchUser();
}

async function validateCoupon(coupon: string) {
  return (await callPostApi('user/service/subs/coupon/', { coupon })) as Coupon;
}

async function getStripeSetupIntentScrete() {
  return await callPostApi<string>(
    'user/service/subs/stripe/setup_intent/client_secret/'
  );
}

async function setStripeDefaultPaymentMethod(payment_method_id: string) {
  return await callPostApi(
    'user/service/subs/stripe/set_default_payment_method/',
    { payment_method_id }
  );
}

export function createCheckoutSession(payload: {
  price: string;
  coupon?: string;
  quantity: number;
  company_name: string;
  from_samsung_store?: boolean;
}) {
  return callPostApi(
    'user/service/subs/stripe/create_checkout_session/',
    payload
  ) as Promise<{ client_secret: string }>;
}

export async function directPaySubscriptionUpgradeRequest() {
  return await callPostApi('user/service/subs/direct_pay_upgrade_request/', {});
}

export default {
  validateCoupon,
  getAllProducts,
  getCoupons,
  getPaymentResources,
  getUpcomingInvoice,
  estimateInvoice,
  getSubscriptions,
  createOrUpdateSubscription,
  cancelSubscription,
  renew,
  retryPay,
  getUser,
  previewSchedule,
  deleteSchedule,
  getStripeSetupIntentScrete,
  setStripeDefaultPaymentMethod,
  getLatestFailedInvoice,
  directPaySubscriptionUpgradeRequest,
};
